import type { CommonDeps } from '../../../app-core/lib/base/CommonDeps'
import {
    createReportGuideDetailsMap,
    fetchFileGuideDownloadUrls,
    fetchGuideIcons,
} from '../../../app-core/lib/guide/helpers'
import type { AppFileGuideDetails, AppGuide, ParentInfo } from '../../../app-core/lib/guide/Model'
import { type FileExtension, isVideoExtension } from '../../../shared/domain/files/fileExtensions.no-deps'
import type * as File from '../../../shared/domain/files/types.no-deps'
import * as Element from '../../../shared/domain/flows/element.no-deps'
import type * as Flow from '../../../shared/domain/flows/types.no-deps'
import type * as Guide from '../../../shared/domain/guides/types.no-deps'
import type * as Post from '../../../shared/domain/posts/types.no-deps'
import type * as Workflow from '../../../shared/domain/workflows/types.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import { arrayToMap } from '../../../shared/lib/helpers.no-deps'
import { getMimeTypeFromExtension } from '../../../shared/lib/mimeTypes.no-deps'
import { sortAlphabetical } from '../../../shared/lib/string.no-deps'
import { createGuideReportId } from '../Flow/Flow.Report'
import { IDB_filesMeta, IDB_guides, IDB_procedures, IDB_reportGuideDetails } from '../IDB/IDB.dexie'
import { IDB_workflowRead } from '../IDB/IDB.table.workflow'
import { Plat_viewFile } from '../Plat/Plat.device'
import { Rout_toProcedureContentPage } from '../Rout/Rout.procedure'
import { Rout_toUnitContentPage } from '../Rout/Rout.unit'
import { Unit_fetchByIds } from '../Unit/Unit.fetch'
import { WF_fetch, WF_fetchByIds } from '../WF/WF.fetch'

export async function Guid_fetchSyncGuidesByTarget(
    logger: ClientLogger,
    target: Post.Target,
    variant?: Guide.Variant
): Promise<Guide.SyncGuide[]> {
    const isTargededGuide = (syncGuide: Guide.SyncGuide): boolean =>
        (target.procedure && syncGuide.procedures.findIndex((procedure) => procedure === target.procedure) !== -1) ||
        (target.unit && syncGuide.units.findIndex((unit) => unit === target.unit) !== -1)

    logger.log('Fetching sync guides')
    const guides = await IDB_guides()
        .toArray()
        .then((guides) => guides.filter((guide) => !guide.isDeleted))
        .then((syncGuides) => syncGuides.filter(isTargededGuide))
        .then((guides) => sortAlphabetical(guides, (guide) => guide.name))

    // return all guides for the target if variant has not been specified
    if (variant === undefined) {
        return guides
    }

    // return guides based on variant
    if (variant === 'workflow' || variant === 'url' || variant === 'file') {
        return guides.filter((guide) => guide.variant === variant)
    } else {
        logger.warn(`Unregcognized guide variant ${variant}`)
        return []
    }
}

export function Guid_fetchSyncGuidesByIds(
    logger: ClientLogger,
    ids: Guide.Id[],
    includeDeleted: boolean = false
): Promise<Guide.SyncGuide[]> {
    logger.log('Fetching sync guides by IDs')

    return IDB_guides()
        .where('id')
        .anyOf(ids)
        .toArray()
        .then((guides) => guides.filter((guide) => includeDeleted || !guide.isDeleted))
        .then((guides) => sortAlphabetical(guides, (guide) => guide.name))
}

export function Guid_fetchSyncGuidesByTagIds(logger: ClientLogger, tagIds: number[] = []): Promise<Guide.SyncGuide[]> {
    logger.log('Fetching sync guides by tag ID')

    const hasTagId = (tags: Guide.GuideTag[] | string[]) =>
        tags
            .filter((tag: Guide.GuideTag) => tag.id)
            .map((guideTag: Guide.GuideTag) => guideTag.id)
            .filter((tagId) => tagIds.includes(tagId)).length > 0

    return IDB_guides()
        .toArray()
        .then((guides) => guides.filter((guide) => guide.tags && hasTagId(guide.tags) && !guide.isDeleted))
        .then((guides) => sortAlphabetical(guides, (guide) => guide.name))
}

export async function Guid_fetchSyncGuide(logger: ClientLogger, id: Guide.Id): Promise<Guide.SyncGuide> {
    logger.log(`Fetching sync guide with ID: ${id}`)

    const guide = await IDB_guides().get(id)

    if (guide.isDeleted) {
        return Promise.reject(`Guide was deleted: ${id}`)
    }

    return guide
}

export async function Guid_fetchGuideParentInfo(guide: Guide.SyncGuide): Promise<ParentInfo[] | null> {
    let procedureParentInfo: ParentInfo[] = []
    let unitParentInfo: ParentInfo[] = []

    if (guide.procedures.length > 0) {
        const guideProcedures = await IDB_procedures().bulkGet(guide.procedures)
        procedureParentInfo = guideProcedures.map((procedure) => {
            return {
                type: 'procedure',
                id: procedure.id,
                name: procedure.name,
                procedure: {
                    id: procedure.id,
                    name: procedure.name,
                    description: procedure.description,
                },
            }
        })
    }

    if (guide.units.length > 0) {
        const guideUnits = await Unit_fetchByIds(guide.units)
        unitParentInfo = guideUnits.map((unit) => {
            return {
                type: 'unit',
                id: unit.id,
                name: unit.name,
                unit,
            }
        })
    }

    const parentInfo = sortAlphabetical([...procedureParentInfo, ...unitParentInfo], (item: ParentInfo) => item.name)

    return parentInfo.length > 0 ? parentInfo : null
}

export async function Guid_fetchAppGuides(
    deps: CommonDeps,
    logger: ClientLogger,
    syncGuides: Guide.SyncGuide[],
    withParentInfo: boolean
): Promise<AppGuide[]> {
    logger.log('Fetching app guides using sync guides')
    const fetchReportGuideDetailsFn = async (ids: Guide.ReportGuideId[]) => IDB_reportGuideDetails().bulkGet(ids)
    const reportGuideDetailsMap = await createReportGuideDetailsMap(fetchReportGuideDetailsFn, syncGuides)
    const fetchFileMetasFn = (fileIds: File.FileId[]) =>
        IDB_filesMeta()
            .bulkGet(fileIds)
            .then((files) => files.filter(isNotNil))

    const { fileExtensionMap, iconUrlMap } = await fetchGuideIcons(
        deps,
        syncGuides,
        reportGuideDetailsMap,
        fetchFileMetasFn
    )

    const toAppGuide = async (syncGuide: Guide.SyncGuide): Promise<AppGuide> => {
        const parentInfo = withParentInfo && (await Guid_fetchGuideParentInfo(syncGuide))
        const iconUrl = iconUrlMap.get(syncGuide.id)

        switch (syncGuide.variant) {
            case 'file':
                const fileExtension = fileExtensionMap.get(syncGuide.file)

                return {
                    ...syncGuide,
                    iconUrl,
                    parentInfo,
                    isVideoFileGuide: isVideoExtension(fileExtension),
                    extension: fileExtension,
                }
            case 'report':
            case 'url':
                return { ...syncGuide, iconUrl, parentInfo }
            case 'workflow':
                const { numberOfVersions: version } = await WF_fetch(logger, syncGuide.workflow, {
                    includeDeleted: false,
                })
                return { ...syncGuide, version, iconUrl, parentInfo }
        }
    }

    return Promise.all(syncGuides.map(toAppGuide))
}

export async function Guid_fetchFileGuideDetails(deps: CommonDeps, fileId: File.FileId): Promise<AppFileGuideDetails> {
    const [downloadUrls, fileMeta] = await Promise.all([
        fetchFileGuideDownloadUrls(deps, [fileId]),
        IDB_filesMeta().get(fileId),
    ])

    const downloadUrl = downloadUrls[fileId]
    const fileExtension = fileMeta.extension

    return { downloadUrl, fileExtension }
}

export async function Guid_fetchReportGuideDetails(
    reportGuideId: Guide.ReportGuideId
): Promise<Guide.ReportGuideDetails> {
    return IDB_reportGuideDetails().get(reportGuideId)
}

export async function Guid_fetchFileGuideDetailsMap(
    deps: CommonDeps,
    fileIds: File.FileId[]
): Promise<Map<File.FileId, AppFileGuideDetails>> {
    const [downloadUrls, fileMetas] = await Promise.all([
        fetchFileGuideDownloadUrls(deps, fileIds),
        IDB_filesMeta().bulkGet(fileIds),
    ])
    const fileExtensionMap = new Map(fileMetas.map((fileMeta) => [fileMeta.fileId, fileMeta.extension]))

    const detailsMap = new Map<File.FileId, AppFileGuideDetails>()
    for (const fileId of fileIds) {
        const downloadUrl = downloadUrls[fileId]
        if (downloadUrl) {
            const fileExtension = fileExtensionMap.get(fileId)
            detailsMap.set(fileId, {
                downloadUrl,
                fileExtension,
            })
        }
    }

    return detailsMap
}

export function Guid_downloadFile(downloadUrl: string, fileExtension: FileExtension, name: string): Promise<void> {
    const fileMimeType = getMimeTypeFromExtension(fileExtension)

    return Plat_viewFile(downloadUrl, name, fileMimeType)
}

export async function getRequiredGuideElementsMap(
    logger: ClientLogger,
    flow: Flow.Flow
): Promise<Map<Guide.Id, Guide.SyncGuide>> {
    // Get guideIds of all required guide elements in flow
    const guideIds = Element.getGuideElementIds(flow, true)

    // Get syncGuides against the guideId from indexed db
    const syncGuides = await Guid_fetchSyncGuidesByIds(logger, guideIds, true)

    // Create a map and return
    return pipe(syncGuides, (guide) => arrayToMap(guide, 'id'))
}

export async function getGuideElementsContextMap(
    logger: ClientLogger,
    flow: Flow.Flow
): Promise<Map<Guide.Id, { guide: Guide.SyncGuide; workflow: Workflow.WorkflowReference }>> {
    // Get all guide IDs (required and non-mandatory) associated to a flow.
    const guideIds = Element.getGuideElementIds(flow, false)
    const tagIds: number[] = Element.getDynamicGuideElementIds(flow).map((e) => e.dynamicGuideSettings.tagId)
    const ids = (await Guid_fetchSyncGuidesByTagIds(logger, tagIds)).map((e) => e.id)
    // Get syncGuides against the guideId from indexed db
    const syncGuides = await Guid_fetchSyncGuidesByIds(logger, [...guideIds, ...ids], true)

    const workflowIds = syncGuides
        .filter((guide) => guide.variant === 'workflow' && guide.workflow)
        .map((guide) => guide.workflow)

    const workflows = workflowIds.length ? await WF_fetchByIds(logger, workflowIds, true) : []
    const guidesWithWorkflow = syncGuides.map((guide) => ({
        id: guide.id,
        guide,
        workflow: workflows.find((workflow) => workflow.id === guide.workflow),
    }))

    // Create a map and return
    return pipe(guidesWithWorkflow, (guideWithWorkflow) => arrayToMap(guideWithWorkflow, 'id'))
}

export function Guid_routeToGuideParentPage(parentInfo: ParentInfo, routeToReturnHere: () => void) {
    switch (parentInfo.type) {
        case 'procedure':
            return Rout_toProcedureContentPage(parentInfo.procedure.id, routeToReturnHere)
        case 'unit':
            return Rout_toUnitContentPage(parentInfo.unit.id, {}, routeToReturnHere)
    }
}

export async function Guid_fetchWorkflowGuideReportId(
    logger: ClientLogger,
    workflowId: number,
    parentClientId?: Flow.ReportId
): Promise<Flow.ReportId> {
    return IDB_workflowRead(workflowId).then((workflow: Workflow.AppFlowWorkflow) =>
        createGuideReportId(logger, workflow, {
            parentClientId,
            progenitorClientId: parentClientId,
        })
    )
}
